<template>
  <div class="bidder-list-wrap flex-column">
    <div class="list-filter flex-col col-center">
      <a-radio-group
        v-model="bidderFilterForm.scoreStatus"
        class="m-b-20"
        @change="changeStatus"
      >
        <a-radio-button value="">全部</a-radio-button>
        <a-radio-button
          v-for="item in companyReviewStatusDictArray"
          :key="item.value"
          :value="item.value"
        >
          {{ getFilterButtonText(item) }}
        </a-radio-button>
      </a-radio-group>
      <a-input-search
        v-model="bidderFilterForm.supplierName"
        placeholder="请输入投标单位名称"
        style="width: 100%; height: 32px;"
        @search="handleSearch"
      />
    </div>
    <el-scrollbar ref="bidderScroll" class="bidder-list flex-1">
      <div
        v-for="(item, index) in bidderList"
        :key="item.supplierId"
        class="bidder-list-item"
        :class="{
          'is-active': currentBidder.supplierId === item.supplierId
        }"
        @click="changeCurrentBidder(index)"
      >
        <div class="bidder-list-item__main">
          <div
            class="supplier-name"
            :class="companyReviewStatusDict.translate(item.scoreStatus).type"
          >
            {{ item.supplierName }}
          </div>

          <div
            class="review-status"
            :class="getScoreStatusEntry(item.scoreStatus).type"
          >
            {{ getScoreStatusEntry(item.scoreStatus).label }}
          </div>
        </div>
        <div class="bidder-list-item__sub m-t-8">
          <div class="sub-info">
            <span v-if="item.offerRate">企业折扣率 {{ item.offerRate }}%</span>
          </div>
          <template
            v-if="item.scoreStatus !== companyReviewStatusDict.PENDING.value"
          >
            <span v-if="showScore">({{ item.score }}分)</span>
            <span v-else>
              {{ Number(item.score) ? '(通过)' : '(不通过)' }}
            </span>
          </template>
        </div>
      </div>
    </el-scrollbar>
  </div>
</template>

<script>
import { getInquiryReviewScorePageApi } from '@/api/procure/inquiry-review'
import { getNegotiationReviewScorePageApi } from '@/api/procure/negotiation-review'
import { companyReviewStatusDict } from '@/dictionary/hall/review-hall'
import {
  inquiryReviewTypeDict,
  onlineReviewBidderScoreStatusDict
} from '@/dictionary/project/online-review'
import { purchaseTypeDict } from '@/dictionary/project/common'
import { getPurchaseReviewSupplierQuoteListApi } from '@/api/procure/purchase-review'

export default {
  name: 'InquiryReviewBidderList',

  inject: ['quoteRateChange'],

  props: {
    reviewId: {
      type: [String, Number],
      default: ''
    },

    orderId: {
      type: [String, Number],
      default: ''
    },

    // 当前的评审办法id
    wayId: {
      type: [String, Number],
      default: ''
    },

    // 当前投标单位信息
    bidder: {
      type: Object,
      default: () => {}
    },

    showScore: {
      type: Boolean,
      default: false
    },

    reviewer: {
      type: Object,
      default: () => {}
    },

    purchaseType: {
      type: [Number, String],
      default: purchaseTypeDict.INQUIRY.value
    },

    reviewWay: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      companyReviewStatusDict,

      bidderIndex: 0,

      // 投标列表统计信息
      bidderStatistics: {
        notScoreCount: 0,
        scoredCount: 0
      },

      bidderFilterForm: {
        scoreStatus: '',
        supplierName: ''
      },

      bidderList: [],

      companyReviewStatusDictArray: [
        { label: '待评', value: 0 },
        { label: '已评', value: 1 }
      ]
    }
  },

  computed: {
    // 当前评审中的投标单位
    currentBidder() {
      return this.bidderList[this.bidderIndex] || {}
    },

    getReviewScorePageApi() {
      const objApi = {
        [purchaseTypeDict.INQUIRY.value]: getInquiryReviewScorePageApi,
        [purchaseTypeDict.NEGOTIATION.value]: getNegotiationReviewScorePageApi
      }
      return objApi[this.purchaseType]
    },

    isQuotationReview() {
      return this.reviewWay.wayType === inquiryReviewTypeDict.QUOTATION.value
    }
  },

  watch: {
    bidder: {
      handler(val) {
        if (val?.supplierId) {
          this.bidderIndex = this.bidderList.findIndex(
            item => item.supplierId === val.supplierId
          )
        }
      },
      deep: true
    },

    wayId: {
      handler(val) {
        if (val) {
          this.getBidderList().then(() => {
            // 默认选中第一家单位
            this.changeCurrentBidder(0)
          })
        }
      },
      immediate: true
    },

    quoteRateChange: {
      handler() {
        this.getBidderList()
      },
      deep: true
    }
  },

  methods: {
    changeStatus() {
      this.getBidderList().then(() => {
        // 默认选中第一家单位
        this.changeCurrentBidder(0)
      })
    },

    handleSearch() {
      this.getBidderList().then(() => {
        // 默认选中第一家单位
        this.changeCurrentBidder(0)
      })
    },

    getBidderList() {
      const params = {
        isListing: 1,
        orderId: this.orderId,
        wayId: this.wayId,
        ...this.bidderFilterForm
      }
      if (this.reviewer?.reviewerId) {
        params.reviewerId = this.reviewer.reviewerId
      }

      return new Promise(resolve => {
        this.getReviewScorePageApi(this.reviewId, params)
          .then(res => {
            this.bidderList = res.data?.records || []
            this.getBidderStatistic()
            if (this.isQuotationReview) {
              this.getQuoteRateList()
            }
          })
          .then(() => {
            resolve(this.bidderList)
          })
          .catch(err => {
            if (err?.message) this.$message.error(err.message)
          })
      })
    },

    getBidderStatistic() {
      const params = {
        orderId: this.orderId,
        page: 1,
        size: 0,
        scoreStatus: 0,
        wayId: this.wayId
      }
      if (this.reviewer?.reviewerId) {
        params.reviewerId = this.reviewer.reviewerId
      }
      const requestList = [
        this.getReviewScorePageApi(this.reviewId, params),
        this.getReviewScorePageApi(this.reviewId, {
          ...params,
          scoreStatus: 1
        })
      ]
      Promise.allSettled(requestList).then(res => {
        if (res[0].status === 'fulfilled') {
          this.bidderStatistics.notScoreCount = res[0].value?.data?.total || 0
        }
        if (res[1].status === 'fulfilled') {
          this.bidderStatistics.scoredCount = res[1].value?.data?.total || 0
        }
      })
    },

    /**
     * 获取投标人列表筛选按钮的文案
     */
    getFilterButtonText(item) {
      const count =
        item.value === 0
          ? this.bidderStatistics.notScoreCount || 0
          : this.bidderStatistics.scoredCount || 0
      return `${item.label}(${count})`
    },

    /**
     * 切换当前评审中的投标单位
     */
    changeCurrentBidder(index) {
      console.log('emit bidder')
      this.$emit('changeBidder', this.bidderList[index])
    },

    /**
     * 获取评审状态
     */
    getScoreStatusEntry(scoreStatus) {
      return companyReviewStatusDict.translate(scoreStatus) || {}
    },

    /**
     * 选择下一个评审单位
     */
    nextBidder() {
      if (this.bidderIndex < this.bidderList.length - 1) {
        this.bidderIndex++
        this.changeCurrentBidder(this.bidderIndex)
      } else {
        this.$message.warning('当前已经是最后一个投标单位')
      }
    },

    /**
     * 判断是否所有投标单位都完成评审
     */
    validateAllScored() {
      return !this.bidderList.some(
        bidder =>
          bidder.scoreStatus === onlineReviewBidderScoreStatusDict.PENDING.value
      )
    },

    /**
     * 获取报价折扣率列表
     */
    getQuoteRateList() {
      getPurchaseReviewSupplierQuoteListApi(this.reviewId)
        .then(res => {
          const _rateList = res.data || []
          _rateList.forEach(rateItem => {
            const bidder = this.bidderList.find(
              bidder => bidder.supplierId === rateItem.supplierId
            )
            if (bidder) {
              bidder.offerRate = rateItem.offerRate
            }
          })
          this.$forceUpdate()
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    }
  }
}
</script>

<style scoped lang="scss">
.bidder-list-wrap {
  text-align: center;
  overflow: hidden;

  .list-filter {
    padding: 24px 30px;
  }

  ::v-deep .el-scrollbar__wrap {
    overflow-x: hidden;
  }

  .bidder-list {
    .bidder-list-item {
      padding: 14px 24px;
      display: flex;
      flex-direction: column;
      cursor: pointer;

      &.is-active {
        background-color: #eaeaea;
        color: #333333;
        font-weight: bold;
      }

      .bidder-list-item__main {
        display: flex;
        align-items: stretch;
        justify-content: space-between;

        .supplier-name {
          text-align: left;
        }

        .review-status {
          font-size: 12px;
          word-break: keep-all;
          margin-left: 12px;
          &.primary {
            color: #1890ff;
          }
          &.info {
            color: #999999;
          }
        }
      }

      .bidder-list-item__sub {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 12px;
        color: #999999;
      }
    }
  }
}
</style>
